import React, { useEffect } from 'react';

/**
 * Google Customer Reviews opt-in badge.
 *
 * Renders after a successful eSIM purchase. Loads the Google platform.js
 * script on demand (idempotent — safe to mount many times) and passes real
 * order data so Google can email the customer a review invitation.
 *
 * Google Merchant Center configuration:
 *   merchant_id: 5830611479
 *   Contact merchant if this changes.
 *
 * Props:
 *   orderId  — Connectify order id from /esim/generate.
 *   email    — customer email.
 *   country  — ISO 2-letter country code (fallback AU).
 *   deliveryDate — YYYY-MM-DD string (today for digital delivery).
 *   products — optional list of {gtin} objects for product ratings.
 */
const GOOGLE_MERCHANT_ID = 5830611479;
const PLATFORM_JS_SRC = 'https://apis.google.com/js/platform.js?onload=renderOptIn';
const SCRIPT_ID = 'google-customer-reviews-platform-js';

const GoogleCustomerReviewsOptin = ({
  orderId,
  email,
  country,
  deliveryDate,
  products,
}) => {
  useEffect(() => {
    if (!orderId || !email) return undefined;

    const deliveryCountry = (country || 'AU').toUpperCase().slice(0, 2);
    const estimatedDeliveryDate =
      deliveryDate || new Date().toISOString().slice(0, 10);

    // Callback Google's platform.js invokes once loaded.
    window.renderOptIn = function renderOptIn() {
      if (!window.gapi) return;
      window.gapi.load('surveyoptin', function () {
        try {
          window.gapi.surveyoptin.render({
            merchant_id: GOOGLE_MERCHANT_ID,
            order_id: String(orderId),
            email: String(email),
            delivery_country: deliveryCountry,
            estimated_delivery_date: estimatedDeliveryDate,
            ...(Array.isArray(products) && products.length > 0 ? { products } : {}),
          });
        } catch (err) {
          // Silent — badge is non-critical; no user-facing error.
          console.debug('Google Customer Reviews render failed', err);
        }
      });
    };

    // If gapi + surveyoptin are already loaded (script cached from a prior
    // purchase in the same session), fire immediately.
    if (window.gapi && window.gapi.load) {
      window.renderOptIn();
      return undefined;
    }

    // Otherwise inject the script once.
    if (!document.getElementById(SCRIPT_ID)) {
      const script = document.createElement('script');
      script.id = SCRIPT_ID;
      script.src = PLATFORM_JS_SRC;
      script.async = true;
      script.defer = true;
      document.head.appendChild(script);
    }

    return undefined;
  }, [orderId, email, country, deliveryDate, products]);

  // The opt-in badge is rendered by Google into its own iframe. This
  // container is intentionally invisible to layout — Google positions the
  // badge itself (usually a small floating card).
  return <div data-testid="google-customer-reviews-optin" aria-hidden="true" />;
};

export default GoogleCustomerReviewsOptin;
